import React, { memo, useMemo } from "react";
import Chart from "react-apexcharts";


type DonutChartProps = {
  labels: string[];
  series: number[];
  colors: string[];
};
export const DonutChart: React.FC<DonutChartProps> = memo(({ labels, series, colors }) => {
  // Defensive resolve for Chart component to avoid undefined element type at runtime
  const ChartComponent: any = (Chart as any)?.default ?? (Chart as any);

  // Normalize inputs and prevent rendering when there is no actual data
  const safeSeries = useMemo(
    () => (Array.isArray(series) ? series.map((n) => (typeof n === 'number' && isFinite(n) ? n : 0)) : []),
    [series]
  );
  const total = useMemo(() => safeSeries.reduce((a, b) => a + b, 0), [safeSeries]);

  // Align labels length to series length to avoid library mismatches
  const safeLabels = useMemo(
    () => (Array.isArray(labels) ? labels.slice(0, safeSeries.length).map((l:any)=>String(l)) : []),
    [labels, safeSeries.length]
  );

  // Key depends only on arrays length to avoid Apex stale refs when dataset shape changes
  const chartKey = useMemo(() => `${safeLabels.length}-${safeSeries.length}`,[safeLabels.length, safeSeries.length]);

  const options = useMemo(() => ({
    chart: {
      id: "basic-donut",
      animations: { enabled: false },
      toolbar: {
        show: false
      }
    },
    labels: safeLabels,
    colors: colors,
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              showAlways: true,
              label: 'Total',
              formatter: () => total.toLocaleString()
            }
          }
        }
      }
    },
    legend: {
      show: true,
      position: 'right' as 'right',
      fontSize: '14px',
      itemMargin: {
        vertical: 8
      },
      onItemClick: {
        toggleDataSeries: false
      },
      formatter: function(seriesName: string, opts: any) {
        const t = opts.w.globals.series.reduce((acc: number, val: number) => acc + val, 0);
        const value = opts.w.globals.series[opts.seriesIndex].toLocaleString();
        const percentage = parseFloat(((opts.w.globals.series[opts.seriesIndex] / t) * 100).toFixed(1));
        return `${value} • ${isNaN(percentage) ? 0 : percentage}%<br>${seriesName}`;
      }
    },
    dataLabels: {
      enabled: true,
      style: {
        fontSize: '10px'
      }
    },
    tooltip: {
      theme: "dark",
      marker: {
        show: true,
      },
      y: {
        formatter: function(val: number) {
          return val.toLocaleString();
        }
      }
    },
    responsive: [{
      breakpoint: 768,
      options: {
        legend: {
          position: 'bottom' as 'bottom',
          fontSize: '10px',
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                total: {
                  fontSize: '10px'
                },
                value: {
                  fontSize: '10px',
                  offsetY: -2
                }
              }
            }
          }
        },
      }
    }]
  }), [safeLabels, colors, total]);

  // Ensure the resolved value is a valid React component (function/class)
  if (!ChartComponent || typeof ChartComponent !== 'function') {
    return null;
  }

  if (!safeSeries.length || total === 0) {
    return (
      <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
        データがありません
      </div>
    );
  }

  return <ChartComponent key={chartKey} options={options} series={safeSeries} type="donut" />;
});